import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  Flex,
  Checkbox,
  Table,
  AutoComplete,
  App,
  Select,
  Popover,
  message,
  Input,
  Grid,
  Tag,
} from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import InputNumberField from "../../../../components/Input/InputNumber";
import { useSelector, useDispatch } from "react-redux";
import { fetchProductsList } from "../../../Products/productsThunks";
import { selectDefaultAccount } from "../../salesSelectors";
import DataTableFooter from "../../../../components/DataTableFooter/DataTableFooter";
import { useNavigate } from "react-router-dom";
import {
  fetchSpecialPrices,
  fetchCurrentStock,
  fetchBatchesForItem,
  fetchPackingStructureForItem,
  fetchAvailableTatmeenSerials,
} from "../../salesThunks";
import { getCurrencySymbol } from "../../../../lib/currency";
import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import { selectItems } from "../../../../store/commonSelectors/lookupsSelectors";
import ProductQuickDrawer from "../../../Product_manage/components/drawers/ProductQuickDrawer";
import { bigMul, bigPercent, toBig } from "../../../../lib/bigConversion";
import { accessChecker } from "../../../../lib/functions";
import { getItemDiscountAmount } from "../../../../utils/sale";
import { getItemDisplayName } from "../../../../utils";

const InvoiceTable = ({
  taxMode,
  handleAddRow,
  handleDeleteRow,
  handleFooterChange,
  handleInputChange,
  handleSaveDraftAndNavigate,
  footerValues,
  itemsList,
  authInfo,
  selectedSearchedCustomer,
  conversionRate,
  dueDate,
  convertId,
  quoteId,
  editId,
  isEditMode,
  isTatmeenProduct = false,
  isTatmeenUAE = false,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.sm;
  const lookups = useSelector((state) => state.lookups);
  const { loading, specialPrices, batchesList, packingStructure } = useSelector(
    (state) => state.sales
  );
  const defaultAccount = useSelector(selectDefaultAccount);
  const currency = authInfo?.currency || "AED";

  const { modal } = App.useApp();
  const [packingMap, setPackingMap] = useState({});
  const [batchMap, setBatchMap] = useState({});
  const [productDrawer, setProductDrawer] = useState({ open: false, rowKey: null });
  console.log(batchMap, "batchMap1111");

  const showTatmeenColumn =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  const symbol = getCurrencySymbol(
    conversionRate ? footerValues?.conversion_currency : currency
  );
  const items = useSelector(selectItems);

  const hasPackingOptions = itemsList?.some(
    (item) => item?.packing_options?.length > 0 || item?.packing_name
  );
console.log(hasPackingOptions,itemsList,"hasPackingOptions");

  const isIndia = authInfo?.country?.toLowerCase() === "india";
  const taxLabel = isIndia ? "GST" : "VAT";
  const gstEnabled = authInfo?.gst_enabled === "yes";
  const showTax = !isIndia || gstEnabled;

  // ─── ✅ getRateForCustomer with isMohItem flag (same as Sales Pharma) ──────────
  const getRateForCustomer = (item, partyType, isMohItem = false) => {
    if (isMohItem) {
      return parseFloat(item.moh_invoice_rate) || parseFloat(item.sale_rate) || 0;
    }
    switch (partyType) {
      case "wholesaler":
        return parseFloat(item.wholesaler_rate) || parseFloat(item.sale_rate) || 0;
      case "retailer":
        return parseFloat(item.retailer_rate) || parseFloat(item.sale_rate) || 0;
      case "distributor":
        return parseFloat(item.distributor_rate) || parseFloat(item.sale_rate) || 0;
      case "corporate":
        return parseFloat(item.corporate_rate) || parseFloat(item.sale_rate) || 0;
      default:
        return parseFloat(item.sale_rate) || 0;
    }
  };

  // ─── ✅ Re-evaluate rates when bonus rows change (ported from Sales Pharma) ────
  const isRateUpdateInProgress = useRef(false);
  const prevBonusItemIdsRef = useRef(new Set());

  useEffect(() => {
    if (isRateUpdateInProgress.current) return;

    const bonusItemIds = new Set(
      itemsList
        .filter((row) => row.bonus && row.product?.item_id)
        .map((row) => row.product.item_id)
    );

    const prevBonusItemIds = prevBonusItemIdsRef.current;
    const bonusSetChanged =
      bonusItemIds.size !== prevBonusItemIds.size ||
      [...bonusItemIds].some((id) => !prevBonusItemIds.has(id));

    // Only re-sync rates when the set of bonus-linked products actually
    // changed. Otherwise this effect fires on every itemsList update
    // (including a manual rate edit) and, seeing the edited rate differ
    // from the computed customer rate, immediately stomps it back.
    if (!bonusSetChanged) return;

    let needsUpdate = false;

    itemsList.forEach((row) => {
      if (!row.bonus && row.product?.item_id) {
        const shouldUseMoh = bonusItemIds.has(row.product.item_id);
        const wasUsingMoh = prevBonusItemIds.has(row.product.item_id);

        // Only rows whose moh/bonus status actually flipped need
        // re-syncing — never touch a row the user has manually priced.
        if (shouldUseMoh === wasUsingMoh) return;

        const currentProduct = row.product;
        const partyType = selectedSearchedCustomer?.party_type || "general";

        const expectedRate = shouldUseMoh
          ? parseFloat(currentProduct.moh_invoice_rate) ||
          parseFloat(currentProduct.sale_rate) ||
          0
          : getRateForCustomer(currentProduct, partyType, false);

        if (parseFloat(row.rate) !== parseFloat(expectedRate)) {
          needsUpdate = true;
          isRateUpdateInProgress.current = true;

          handleInputChange(expectedRate, row.key, "rate");

          const taxAmount = bigPercent(expectedRate, currentProduct?.tax_per || 0);
          handleInputChange(taxAmount || 0, row.key, "vat");
        }
      }
    });

    prevBonusItemIdsRef.current = bonusItemIds;

    if (needsUpdate) {
      setTimeout(() => {
        isRateUpdateInProgress.current = false;
      }, 100);
    }
  }, [itemsList]);

  const fetchProducts = useCallback(
    (searchText) => {
      dispatch(fetchProductsList({ query: searchText, search: true, partyType: "general" }));
    },
    [dispatch]
  );

  // ─── Helper: fetch batches ────────────────────────────────────────────────────
  const fetchBatchesForProduct = async (itemId, quantity, rowKey) => {
    if (itemId) {
      try {
        const payload = {
          item_id: itemId,
          item_qty: quantity || 1
        };

        // ✅ If in edit mode, pass the sale_id
        if (editId) {
          payload.sale_id = editId;
        }

        const resBatch = await dispatch(
          fetchBatchesForItem(payload)
        ).unwrap();
        setBatchMap((prev) => ({ ...prev, [itemId]: resBatch || [] }));
      } catch (err) {
        console.error("Error fetching batches:", err);
      }
    }
  };

  // ─── ✅ handleProductSelect with bonus/moh check (same as Sales Pharma) ────────
  const handleProductSelect = async (item, rowKey) => {
    if (!item?.raw) return;
    const value = item.raw;

    try {
      const partyType = selectedSearchedCustomer?.party_type || "general";

      // ✅ Check if this product already exists as a BONUS row in itemsList
      const existsAsBonus = itemsList.some(
        (row) =>
          row.key !== rowKey &&
          row.product?.item_id === value.item_id &&
          row.bonus === true
      );

      // ✅ Get base rate: moh_invoice_rate if product exists as bonus, else party-type rate
      const baseRate = getRateForCustomer(value, partyType, existsAsBonus);

      setPackingMap((prev) => { const m = { ...prev }; delete m[value.item_id]; return m; });
      setBatchMap((prev) => { const m = { ...prev }; delete m[value.item_id]; return m; });

      handleInputChange("", rowKey, "packing_name");
      handleInputChange(0, rowKey, "packing_name_quantity");
      handleInputChange(0, rowKey, "packing_quantity");
      handleInputChange(0, rowKey, "quantity");
      handleInputChange("", rowKey, "batch_name");
      handleInputChange(0, rowKey, "batch_id");
      handleInputChange(null, rowKey, "batch_expiry");
      // Pre-fill the line description from the product master's Narration /
      // Description (editable afterwards). Replaces the previous product's text.
      const narration = String(value.narration ?? "").trim();
      handleInputChange(narration, rowKey, "item_description");
      handleInputChange(!!narration, rowKey, "showDescription");

      handleInputChange([], rowKey, "availableSerials");
      handleInputChange([], rowKey, "selectedSerials");
      handleInputChange("unit", rowKey, "serial_mode");
      handleInputChange(0, rowKey, "available_qty");
      handleInputChange(false, rowKey, "is_tatmeen_required");
      handleInputChange("", rowKey, "child_sscc_code");

      const payload = { item_id: value.item_id, account_id: selectedSearchedCustomer?.acc_id };
      const res = await dispatch(fetchSpecialPrices(payload)).unwrap();
      const data = res || [];

      // ✅ Special price > moh_invoice_rate/party rate
      const finalRate = data.length > 0 && data[0]?.price ? data[0].price : baseRate;

      dispatch(fetchCurrentStock({ itemId: value.item_id, warehouseId: 0 })).then((res) => {
        if (res?.payload) handleInputChange(res.payload, rowKey, "itemInfo");
      });

      const resPack = await dispatch(
        fetchPackingStructureForItem({ product_id: value.item_id })
      ).unwrap();
      setPackingMap((prev) => ({ ...prev, [value.item_id]: resPack || [] }));

      await fetchBatchesForProduct(value.item_id, 1, rowKey);

      handleInputChange(value, rowKey, "product");
      handleInputChange(finalRate || 0, rowKey, "rate");
      handleInputChange(value?.tax_per || 0, rowKey, "vat_per");

      const taxAmount = bigPercent(finalRate, value?.tax_per || 0);
      handleInputChange(taxAmount || 0, rowKey, "vat");
    } catch (err) {
      console.error("Product select failed:", err);
      handleInputChange(value.sale_rate || 0, rowKey, "rate");
    }
  };

  // ─── Sync packing/batch maps from itemsList ───────────────────────────────────
  useEffect(() => {
    if (itemsList.length > 0) {
      const newPackingMap = { ...packingMap };
      const newBatchMap = { ...batchMap };
      let hasUpdates = false;

      itemsList.forEach((item) => {
        if (item.product?.item_id) {
          if (item.packing_options?.length > 0 && !newPackingMap[item.product.item_id]) {
            newPackingMap[item.product.item_id] = item.packing_options;
            hasUpdates = true;
          }
          if (item.batches?.length > 0 && !newBatchMap[item.product.item_id]) {
            newBatchMap[item.product.item_id] = item.batches;
            hasUpdates = true;
          }
        }
      });

      if (hasUpdates) {
        setPackingMap(newPackingMap);
        setBatchMap(newBatchMap);
      }
    }
  }, [itemsList]);

  useEffect(() => {
    if (isEditMode && itemsList.length > 0) {
      itemsList.forEach(async (item) => {
        if (item.product?.item_id && !batchMap[item.product.item_id]) {
          try {
            const quantity = item.quantity > 0 ? item.quantity : 1;
            await fetchBatchesForProduct(item.product.item_id, quantity, item.key);
          } catch (error) {
            console.error(`Failed to fetch batches for item ${item.product.item_id}:`, error);
          }
        }
      });
    }
  }, [isEditMode, itemsList]);

  // ─── Filtered product options (no duplicates) ─────────────────────────────────
  const getFilteredProductOptions = (currentRow) => {
    return items
      // Asset items are Purchase Bill-only (non-stock, routed to a Fixed Asset
      // account) -- never offer them for sale.
      .filter((item) => item.is_asset !== "yes")
      .filter((item) => {
        return !itemsList.some((row) => {
          if (row.key === currentRow.key) return false;
          const sameProduct = row.product?.item_id === item.item_id;
          const sameBatch = (row.batch_id || 0) === (currentRow.batch_id || 0);
          const sameBonus = Boolean(row.bonus) === Boolean(currentRow.bonus);
          return sameProduct && sameBatch && sameBonus;
        });
      });
  };

  // ─── Batch Name Cell ──────────────────────────────────────────────────────────
  const BatchNameCell = ({ record }) => {
    const rawBatches = record.batches || batchMap[record?.product?.item_id] || [];
    const batches = Array.isArray(rawBatches) ? rawBatches : [];

    const options = batches.map((b) => ({
      label: `${b.batch_name} (Avail: ${b.quantity ?? 0})`,
      value: b.batch_name,
      id: b.id,
      obj: b,
    }));

    const [isLoadingSerials, setIsLoadingSerials] = React.useState(false);

    // ✅ Ensure current batch is in options
    React.useEffect(() => {
      if (record.batch_id && record.batch_name && batches.length > 0) {
        const exists = batches.some(b => b.id === record.batch_id);
        if (!exists) {
          const currentBatch = {
            id: record.batch_id,
            batch_name: record.batch_name,
            batch_expiry: record.batch_expiry,
            quantity: record.quantity || 0,
            serial_mode: record.serial_mode || "unit"
          };
          // Add current batch to the list
          const updatedBatches = [currentBatch, ...batches];
          handleInputChange(updatedBatches, record.key, "batches");
        }
      }
    }, [record.batch_id, record.batch_name]);

    // ✅ Load serials for edit mode
    React.useEffect(() => {
      const loadSerialsForEdit = async () => {
        if (record.batch_id && record.product?.item_id &&
          (!record.availableSerials || record.availableSerials.length === 0) &&
          !isLoadingSerials) {
          try {
            setIsLoadingSerials(true);

            if (authInfo?.is_tatmeen_enabled === "yes") {
              const serials = await dispatch(
                fetchAvailableTatmeenSerials({
                  item_id: record.product?.item_id,
                  batch_id: record.batch_id,
                })
              ).unwrap();
              if (serials && serials.length > 0) {
                handleInputChange(serials, record.key, "availableSerials");
              }
            } else {
              const serials = [];
              // const serials = await dispatch(
              //   fetchAvailableSerials({
              //     item_id: record.product?.item_id,
              //     batch_id: record.batch_id,
              //   })
              // ).unwrap();
              if (serials && serials.length > 0) {
                handleInputChange(serials, record.key, "availableSerials");
              }
            }
          } catch (err) {
            console.error("Failed to load serials for edit:", err);
          } finally {
            setIsLoadingSerials(false);
          }
        }
      };

      loadSerialsForEdit();
    }, [record.batch_id, record.product?.item_id]);

    if (record.product?.is_service === "yes") {
      return <span style={{ color: "#999" }}>N/A</span>;
    }

    return (
      <div style={{ position: "relative" }}>
      <Select
        style={{ width: "100%", minWidth: 100 }}
        showSearch
        allowClear
        options={options}
        placeholder={isLoadingSerials ? "Loading serials..." : "Select Batch"}
        value={record.batch_name || undefined}
        optionLabelProp="value"
        optionFilterProp="label"
        filterOption={(inputValue, option) =>
          String(option.label).toUpperCase().includes(inputValue.toUpperCase())
        }
        onSelect={async (value, option) => {
          handleInputChange(option.value, record.key, "batch_name");
          handleInputChange(option.id || 0, record.key, "batch_id");
          handleInputChange(option.obj?.batch_expiry || null, record.key, "batch_expiry");
          handleInputChange(true, record.key, "is_expiry_disable");
          handleInputChange(option.obj.quantity, record.key, "available_qty");
          handleInputChange(option.obj.serial_mode, record.key, "serial_mode");

          handleInputChange([], record.key, "selectedSerials");

          if (option.id && record.product?.item_id) {
            try {
              setIsLoadingSerials(true);

              if (authInfo?.is_tatmeen_enabled === "yes") {
                const serials = await dispatch(
                  fetchAvailableTatmeenSerials({
                    item_id: record.product?.item_id,
                    batch_id: option.id,
                  })
                ).unwrap();
                if (serials && serials.length > 0) {
                  handleInputChange(serials, record.key, "availableSerials");
                } else {
                  handleInputChange([], record.key, "availableSerials");
                  message.info("No serial numbers available for this batch");
                }
              } else {
                // const serials = await dispatch(
                //   fetchAvailableSerials({
                //     item_id: record.product?.item_id,
                //     batch_id: option.id,
                //   })
                // ).unwrap()
                const serials = [];
                if (serials && serials.length > 0) {
                  handleInputChange(serials, record.key, "availableSerials");
                } else {
                  handleInputChange([], record.key, "availableSerials");
                  message.info("No serial numbers available for this batch");
                }
              }
            } catch (err) {
              console.error("Failed to load serials:", err);
              handleInputChange([], record.key, "availableSerials");
              message.error("Failed to load serial numbers");
            } finally {
              setIsLoadingSerials(false);
            }
          }

          if (option.obj.serial_mode === "batch") {
            handleInputChange("batch", record.key, "serial_mode");
            handleInputChange(
              Number(option.obj.quantity || 0),
              record.key,
              "quantity"
            );
            handleInputChange(
              Number(option.obj.quantity || 0),
              record.key,
              "available_qty"
            );
          } else {
            handleInputChange("unit", record.key, "serial_mode");
            handleInputChange(
              Number(option.obj.quantity || 0),
              record.key,
              "available_qty"
            );
            if (Number(record.quantity || 0) > Number(option.obj.quantity || 0)) {
              handleInputChange(1, record.key, "quantity");
            }
          }

          handleInputChange([], record.key, "selectedSerials");
        }}
        onClear={() => {
          // A strict Select can only become empty via the clear button (no
          // free-typing to reconcile like the old AutoComplete needed) — full
          // reset of everything the batch selection had populated.
          handleInputChange("", record.key, "batch_name");
          handleInputChange(0, record.key, "batch_id");
          handleInputChange(null, record.key, "batch_expiry");
          handleInputChange(false, record.key, "is_expiry_disable");
          handleInputChange([], record.key, "availableSerials");
          handleInputChange([], record.key, "selectedSerials");
          handleInputChange("unit", record.key, "serial_mode");
          handleInputChange(0, record.key, "available_qty");
          handleInputChange(0, record.key, "quantity");
        }}
      />
      {record.batch_name && (
        <span
          style={{
            position: "absolute",
            bottom: -18,
            left: 8,
            fontSize: 10,
            padding: "1px 6px",
            borderRadius: 4,
            background: "#f5f5f5",
            color: "#595959",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          Available: {record.available_qty ?? 0}
        </span>
      )}
      </div>
    );
  };

  // ─── Packing Option Cell ──────────────────────────────────────────────────────
  const PackingOptionCell = ({ record }) => {
    const packings = record.packing_options || packingMap[record?.product?.item_id] || [];
    const packingOptions = packings.map((pack) => ({ label: pack.name, value: pack.qty, obj: pack }));
    // Pre-existing bug: this effect referenced `batches`, which only exists
    // in the sibling BatchNameCell's own scope — undefined here, throwing
    // ReferenceError whenever the batch selection changed. Needs its own
    // copy, same derivation BatchNameCell uses.
    const rawBatches = record.batches || batchMap[record?.product?.item_id] || [];
    const batches = Array.isArray(rawBatches) ? rawBatches : [];
    const [inputValue, setInputValue] = React.useState(record.packing_name || "");

    React.useEffect(() => {
      setInputValue(record.packing_name || "");
    }, [record.packing_name]);

    React.useEffect(() => {
      if (record.batch_id && record.batch_name && batches.length > 0) {
        const found = batches.find(b => b.id === record.batch_id);
        if (found && !inputValue) {
          setInputValue(found.batch_name);
          // Also set available serials if any
          if (record.availableSerials && record.availableSerials.length > 0) {
            // Already set via API
          }
        }
      }
    }, [record.batch_id, record.batch_name, batches]);

    return (
      <AutoComplete
        style={{ width: "100%", minWidth: 150 }}
        options={packingOptions}
        placeholder="Select Pack"
        value={inputValue}
        filterOption={(inputValue, option) =>
          String(option.label).toUpperCase().includes(inputValue.toUpperCase())
        }
        onSelect={(value, option) => {
          setInputValue(option.label);
          handleInputChange(option.label, record.key, "packing_name");
          handleInputChange(option.value, record.key, "packing_name_quantity");
          handleInputChange(0, record.key, "packing_quantity");
          if (record?.product?.item_id) {
            fetchBatchesForProduct(record.product.item_id, record.quantity || 1, record.key);
          }
        }}
        onChange={(value) => setInputValue(value)}
        onBlur={() => {
          const found = packings.find((pack) => pack.name === inputValue);
          if (found) {
            handleInputChange(found.name, record.key, "packing_name");
            handleInputChange(found.qty, record.key, "packing_name_quantity");
          } else {
            handleInputChange("", record.key, "packing_name");
            handleInputChange(0, record.key, "packing_name_quantity");
            handleInputChange(0, record.key, "packing_quantity");
          }
        }}
        allowClear
      />
    );
  };

  // ─── Packing Quantity Cell ────────────────────────────────────────────────────
  const PackingQuantityCell = ({ record }) => {
    const [localQty, setLocalQty] = useState(record?.packing_quantity || 0);

    useEffect(() => {
      setLocalQty(record?.packing_quantity || 0);
    }, [record?.packing_quantity]);

    const handleBlur = async () => {
      handleInputChange(localQty, record.key, "packing_quantity");
      if (record.packing_name_quantity && localQty > 0) {
        const totalQuantity = localQty * record.packing_name_quantity;
        handleInputChange(totalQuantity, record.key, "quantity");
        if (record?.product?.item_id) {
          await fetchBatchesForProduct(record.product.item_id, totalQuantity, record.key);
        }
      }
    };

    return (
      <InputNumberField
        min={0}
        controls={false}
        precision={0}
        value={localQty}
        onChange={setLocalQty}
        onBlur={handleBlur}
        style={{ width: "100%" }}
        parser={(val) => val.replace(/\D/g, "")}
        formatter={(val) => val.replace(/\D/g, "")}
        placeholder="Packs"
        disabled={!record.packing_name}
      />
    );
  };

  // ─── ✅ Rate Selector with Popover (same as Sales Doc 4, unchanged) ────────────
  const RateSelector = ({
    record,
    handleInputChange,
    allowEdit,
    symbol,
    convertId,
    quoteId,
    conversionRate,
  }) => {
    const [open, setOpen] = useState(false);
    const warnedRef = useRef(false);

    const product = record.product || {};

    const rates = [
      { label: "Sale Rate", value: product.sale_rate },
      { label: "Corporate Rate", value: product.corporate_rate },
      { label: "Distributor Rate", value: product.distributor_rate },
      { label: "Wholesaler Rate", value: product.wholesaler_rate },
    ].filter((r) => r.value !== null && r.value !== undefined);

    const displayRate = record.bonus
      ? 0
      : conversionRate
        ? (Number(record?.rate) || 0) * Number(conversionRate)
        : Number(record?.rate) || 0;

    const isFullyLocked = !record?.product || !!convertId || !!quoteId;
    const isManualEditAllowed = allowEdit && !isFullyLocked;

    const handleSelect = (value) => {
      if (!isFullyLocked) {
        const finalValue = conversionRate ? value : value;
        handleInputChange(finalValue, record.key, "rate");
        setOpen(false);
      }
    };

    const content = (
      <div style={{ minWidth: 220 }}>
        {rates.map((r, i) => {
          const isSelected = Number(record.rate) === Number(r.value);
          const displayValue = conversionRate ? r.value * conversionRate : r.value;

          return (
            <div
              key={i}
              onClick={() => handleSelect(r.value)}
              style={{
                display: "flex",
                justifyContent: "space-between",
                padding: "8px 12px",
                cursor: isFullyLocked ? "not-allowed" : "pointer",
                borderRadius: 4,
                background: isSelected ? "#e6f7ff" : "transparent",
                fontWeight: isSelected ? "600" : "normal",
                boxShadow: isSelected ? "0 0 0 1px #91d5ff inset" : "none",
                transition: "all 0.2s ease",
                opacity: isFullyLocked ? 0.6 : 1,
              }}
              onMouseEnter={(e) => {
                if (!isSelected && !isFullyLocked) e.currentTarget.style.background = "#f5f5f5";
              }}
              onMouseLeave={(e) => {
                if (!isSelected && !isFullyLocked) e.currentTarget.style.background = "transparent";
              }}
            >
              <span>{r.label}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <strong>
                  {symbol} {Number(displayValue).toFixed(2)}
                </strong>
                {conversionRate && (
                  <span style={{ fontSize: 11, color: "#888" }}>
                    ({Number(r.value).toFixed(2)} base)
                  </span>
                )}
              </div>
              {isSelected && <span style={{ color: "#1890ff", marginLeft: 6 }}>✓</span>}
            </div>
          );
        })}
      </div>
    );

    return (
      <Popover
        content={content}
        trigger="click"
        open={open && !isFullyLocked}
        onOpenChange={(val) => { if (!isFullyLocked) setOpen(val); }}
      >
        <InputNumberField
          min={0}
          step={0.01}
          precision={2}
          controls={false}
          value={displayRate}
          prefix={`${symbol} `}
          parser={(val) => val?.replace(/[^\d.]/g, "") || ""}
          disabled={isFullyLocked}
          onChange={(value) => {
            if (isManualEditAllowed) {
              const finalValue = conversionRate ? value / conversionRate : value;
              handleInputChange(finalValue, record.key, "rate");
            }
          }}
          onKeyDown={(e) => {
            if (!isManualEditAllowed) {
              e.preventDefault();
              if (!warnedRef.current) {
                message.warning("Enable 'Allow Product Rate Edit' in settings to edit manually");
                warnedRef.current = true;
              }
            }
          }}
          onBlur={() => { warnedRef.current = false; }}
          style={{
            width: "100%",
            cursor: isFullyLocked ? "not-allowed" : isManualEditAllowed ? "text" : "pointer",
            background: isManualEditAllowed ? "white" : "#fafafa",
            color: isManualEditAllowed ? "inherit" : "#888",
            caretColor: isManualEditAllowed ? "auto" : "transparent",
          }}
          onClick={() => { if (!isFullyLocked) setOpen(true); }}
        />
      </Popover>
    );
  };

  // ─── Columns ──────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Bonus",
      dataIndex: "bonus",
      rowScope: "row",
      width: 50,
      render: (_, record) => (
        <Checkbox
          checked={record.bonus}
          onChange={(e) => {
            handleInputChange(e.target.checked, record.key, "bonus");
            handleInputChange(null, record.key, "product");
          }}
          style={{ transform: "scale(1.5)", display: "flex", alignItems: "center", justifyContent: "center" }}
        />
      ),
    },
    ...(hasTatmeenEntryAccess && showTatmeenColumn ? [{
      title: "Tatmeen",
      dataIndex: "is_tatmeen_required",
      width: 100,
      render: (_, record) => {
        // Check if Tatmeen is enabled globally AND this is not a quote/order conversion
        const isDisabled = !isTatmeenProduct || convertId || quoteId;

        return (
          <Checkbox
            checked={record.is_tatmeen_required || false}
            disabled={isDisabled}
            onChange={(e) => {
              const checked = e.target.checked;
              handleInputChange(checked, record.key, "is_tatmeen_required");

              // If unchecked, clear Child SSCC and serials
              if (!checked) {
                handleInputChange("", record.key, "child_sscc_code");
                handleInputChange([], record.key, "selectedSerials");
                handleInputChange("unit", record.key, "serial_mode");
              } else {
                // If checked, ensure serial_mode is set
                handleInputChange("unit", record.key, "serial_mode");
                // Initialize serials based on quantity
                const quantity = Number(record.quantity || 0);
                if (quantity > 0) {
                  // Pre-populate with empty slots based on quantity
                  const serials = Array(quantity).fill('');
                  handleInputChange(serials, record.key, "selectedSerials");
                }
              }
            }}
          >
            Required
          </Checkbox>
        );
      },
    }] : []),
    {
      title: "Product",
      dataIndex: "product",
      width: 300,
      onHeaderCell: () => ({ style: { flex: 3 } }),
      render: (_, record) => (
        <div style={{ position: "relative" }}>
          <SearchListAutoComplete
            entityType="item"
            placeholder="Item"
            showAddNew={true}
            style={{ width: "100%" }}
            optionsList={getFilteredProductOptions(record).map((i) => ({
              sale_id: i.item_id,
              display_text: getItemDisplayName(i),
              raw: i,
            }))}
            value={record.product}
            disabled={convertId || quoteId}
            onSelect={async (item) => {
              if (!item || item === null) return;
              if (item === "add_new") {
                setProductDrawer({ open: true, rowKey: record.key });
                return;
              }

              handleInputChange("", record.key, "packing_name");
              handleInputChange(0, record.key, "packing_name_quantity");
              handleInputChange(0, record.key, "packing_quantity");
              handleInputChange(0, record.key, "quantity");
              handleInputChange("", record.key, "batch_name");
              handleInputChange(0, record.key, "batch_id");
              handleInputChange(null, record.key, "batch_expiry");

              // ✅ handleProductSelect now carries bonus/moh check
              handleProductSelect(item, record.key);
              const value = item.raw;

              try {
                const partyType = selectedSearchedCustomer?.party_type || "general";

                // ✅ Check if product exists as bonus row
                const existsAsBonus = itemsList.some(
                  (row) =>
                    row.key !== record.key &&
                    row.product?.item_id === value.item_id &&
                    row.bonus === true
                );

                const baseRate = getRateForCustomer(value, partyType, existsAsBonus);

                const payload = { item_id: value.item_id, account_id: selectedSearchedCustomer?.acc_id };
                const res = await dispatch(fetchSpecialPrices(payload)).unwrap();
                const data = res || [];
                const finalRate = data.length > 0 && data[0]?.price ? data[0].price : baseRate;

                dispatch(fetchCurrentStock({ itemId: value.item_id, warehouseId: 0 })).then((res) => {
                  if (res?.payload) handleInputChange(res.payload, record.key, "itemInfo");
                });

                const resPack = await dispatch(
                  fetchPackingStructureForItem({ product_id: value.item_id })
                ).unwrap();
                setPackingMap((prev) => ({ ...prev, [value.item_id]: resPack || [] }));

                handleInputChange(value, record.key, "product");
                handleInputChange(finalRate || 0, record.key, "rate");
                handleInputChange(value?.tax_per || 0, record.key, "vat_per");
                const taxAmount = bigPercent(finalRate, value?.tax_per || 0);
                handleInputChange(taxAmount || 0, record.key, "vat");
              } catch (error) {
                console.error("Error fetching special price:", error);
                handleInputChange(value.item_rate || 0, record.key, "rate");
              }
            }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {record.product?.item_id && (
              <Button
                type="link"
                size="small"
                style={{ padding: 0 }}
                onClick={() =>
                  handleInputChange(
                    !record.showDescription,
                    record.key,
                    "showDescription",
                  )
                }
              >
                {record.showDescription
                  ? "Hide Description"
                  : "Add Description"}
              </Button>
            )}
            {!convertId && !quoteId && record.product && record.product?.is_service !== "yes" && (() => {
              // Once a batch is picked, what's actually sellable is that
              // batch's own quantity, not the item's total stock across
              // every batch -- showing the total there was misleading (see
              // the "loose quantity available" fix above for the same
              // batch-vs-total distinction).
              const selectedBatch = record.batch_id
                ? (record.batches || batchMap[record.product?.item_id] || []).find(
                  (b) => b.id === record.batch_id
                )
                : null;

              const stockQty = selectedBatch
                ? Number(selectedBatch.quantity ?? 0)
                : Number(record?.itemInfo?.current_qty ?? 0);

              const stockLabel = selectedBatch
                ? `Available Stock for ${selectedBatch.batch_name}: ${stockQty}`
                : `Available Stock: ${stockQty}`;

              return (
                <span
                  style={{
                    marginLeft: "auto",
                    whiteSpace: "nowrap",
                    fontSize: 10,
                    padding: "1px 6px",
                    borderRadius: 4,
                    background: stockQty <= 5 ? "#fff1f0" : "#f5f5f5",
                    color: stockQty <= 5 ? "#cf1322" : "#595959",
                    pointerEvents: "none",
                  }}
                >
                  {stockLabel}
                </span>
              );
            })()}
          </div>

          {record.showDescription && (
            <Input.TextArea
              rows={2}
              placeholder="Add item description"
              value={record.item_description}
              onChange={(e) =>
                handleInputChange(
                  e.target.value,
                  record.key,
                  "item_description"
                )
              }
            />
          )}
        </div>
      ),
    },
    ...(hasPackingOptions || packingStructure?.length > 0
      ? [
        {
          title: "Packing Option",
          dataIndex: "packing_name",
          width: 150,
          render: (_, record) => <PackingOptionCell record={record} />,
        },
        {
          title: "Pack Qty",
          dataIndex: "packing_quantity",
          width: 100,
          render: (_, record) => <PackingQuantityCell record={record} />,
        },
      ]
      : []),
    {
      title: "Quantity",
      dataIndex: "quantity",
      width: 50,
      onHeaderCell: () => ({ style: { flex: 1 } }),
      render: (_, record) => (
        <InputNumberField
          disabled={
            !!record.packing_name ||
            record.serial_mode === "batch"
          }
          min={0}
          controls={false}
          precision={0}
          value={Number(record?.quantity) > 0 ? record.quantity : null}
          onChange={(value) => {
            const qty = Number(value || 0);

            const currentQty = Number(record?.itemInfo?.current_qty || 0);
            const looseQty = Number(record?.itemInfo?.loose_qty || 0);

            const isMinusSale = authInfo.is_minus_stock_sale === "yes";

            // Service lines have no stock to track — skip all stock-availability checks.
            if (record.product?.is_service === "yes") {
              handleInputChange(qty, record.key, "quantity");
              return;
            }

            // -------------------------
            // MINUS STOCK
            // -------------------------

            if (isMinusSale && currentQty <= 0) {

              handleInputChange(qty, record.key, "quantity");
              return;
            }

            // -------------------------
            // BATCH MODE
            // -------------------------

            // if (record.batch_id) {
            //   console.log(record, batchMap, "batchMap0001");

            //   const batch = (record.batches || batchMap[record.product?.item_id] || [])
            //     .find(b => b.id === record.batch_id);

            //   if (batch) {

            //     if (qty > Number(batch.quantity || 0)) {

            //       message.warning(
            //         `Only ${batch.quantity} available in selected batch`
            //       );

            //       return;
            //     }

            //   }

            // }
            if (record.batch_id) {
              // ✅ Use batchMap (has the corrected quantity) instead of record.batches
              const batches = batchMap[record.product?.item_id] || [];
              const batch = batches.find(b => b.id === record.batch_id);

              if (batch) {
                const availableQty = Number(batch.quantity || 0);

                if (qty > availableQty) {
                  message.warning(
                    `Only ${availableQty} available in selected batch`
                  );
                  return;
                }
              }
            }
            else {

              if (qty > looseQty) {

                // When all of this item's stock is batch-controlled,
                // loose_qty (= current_qty - batch_controlled_qty) is
                // genuinely 0 even though the item has real stock -- the
                // user just hasn't picked a batch yet. "Only 0 loose
                // quantity available" reads like the item is out of stock,
                // so point them at the actual fix instead.
                const needsBatchSelection =
                  Boolean(record?.itemInfo?.has_batch_serial) && !record.batch_id;

                message.warning(
                  needsBatchSelection
                    ? "This item's stock is tracked by batch -- please select a batch first"
                    : `Only ${looseQty} loose quantity available`

                );

                return;

              }

            }

            handleInputChange(qty, record.key, "quantity");

          }}
          style={{ width: "100%" }}
          parser={(val) => val.replace(/\D/g, "")}
          formatter={(val) => val.replace(/\D/g, "")}
        />
      ),
    },
    {
      title: "Rate",
      dataIndex: "rate",
      width: 120,
      render: (_, record) => {
        const allowEdit = lookups?.institutionData?.allow_product_rate_change === "yes";
        return (
          <RateSelector
            key={`${record.key}-${conversionRate}`}
            record={record}
            handleInputChange={handleInputChange}
            symbol={symbol}
            allowEdit={allowEdit}
            convertId={convertId}
            quoteId={quoteId}
            conversionRate={conversionRate}
          />
        );
      },
    },
    ...(authInfo?.enable_batch_number !== "no"
      ? [
        {
          title: "Batch Number",
          dataIndex: "batch_name",
          width: 150,
          render: (_, record) => <BatchNameCell record={record} />,
        },
      ]
      : []),
    ...(hasTatmeenEntryAccess && showTatmeenColumn
      ? [
        {
          title: "Child SSCC",
          dataIndex: "child_sscc_code",
          width: 180,
          render: (_, record) => {
            const isDisabled = !record.is_tatmeen_required || !isTatmeenProduct || convertId || quoteId;

            return (
              <Input
                placeholder={record.is_tatmeen_required ? "Required*" : "Optional"}
                value={record.child_sscc_code || ""}
                disabled={isDisabled}
                onChange={(e) =>
                  handleInputChange(
                    e.target.value,
                    record.key,
                    "child_sscc_code"
                  )
                }
                style={{
                  borderColor: record.is_tatmeen_required && !record.child_sscc_code ? '#ff4d4f' : undefined
                }}
              />
            );
          },
        },
        {
          title: "Serial Number",
          dataIndex: "selectedSerials",
          width: 250,
          render: (_, record) => {
            // A service line has no serial-number concept at all, regardless
            // of Tatmeen settings -- takes priority over everything below.
            if (record.product?.is_service === "yes") {
              return <span style={{ color: "#999" }}>N/A</span>;
            }

            // If Tatmeen is not required, show a tag indicating so
            if (!record.is_tatmeen_required) {
              return <Tag color="default">Not Required</Tag>;
            }

            const isDisabled =
              !isTatmeenProduct ||
              convertId ||
              quoteId ||
              !record.product ||
              !(record.availableSerials || []).length;

            return (
              <Select
                mode="multiple"
                style={{ width: "100%" }}
                placeholder={isDisabled ? "Enable Tatmeen Required first" : "Select Serials"}
                value={record.selectedSerials || []}
                options={(record.availableSerials || []).map((serial) => ({
                  label: serial,
                  value: serial,
                }))}
                onChange={(value) => {
                  handleInputChange(value, record.key, "selectedSerials");
                }}
                disabled={isDisabled}
                optionFilterProp="label"
                showSearch
                maxTagCount="responsive"
              />
            );
          },
        }
      ]
      : []),
    ...(taxMode === "individual"
      ? [
        {
          title: "Discount",
          dataIndex: "discount_per",
          width: 160,
          onHeaderCell: () => ({ style: { flex: 1 } }),
          render: (_, record) => {
            const mode = record.discountMode === "amount" ? "amount" : "percent";
            const lineTotal = bigMul(record.quantity, record.rate);
            // One merged compound field (number input + unit-select addon)
            // instead of a separate Select stacked above the number input —
            // reads as a single control and lines up with the rest of the
            // single-line row.
            const modeSelect = (
              <Select
                size="small"
                style={{ width: 65 }}
                disabled={record.bonus}
                value={mode}
                options={[
                  { label: "%", value: "percent" },
                  { label: symbol, value: "amount" },
                ]}
                onChange={(v) => handleInputChange(v, record.key, "discountMode")}
              />
            );
            return mode === "amount" ? (
              <InputNumberField
                min={0}
                max={Number(lineTotal)}
                controls={false}
                precision={2}
                disabled={record.bonus}
                value={record.bonus ? 0 : record.discount_amt}
                onChange={(value) => handleInputChange(value, record.key, "discount_amt")}
                style={{ width: "100%" }}
                addonAfter={modeSelect}
              />
            ) : (
              <InputNumberField
                min={0}
                max={100}
                controls={false}
                precision={2}
                value={record.bonus ? 0 : record.discount_per}
                disabled={record.bonus}
                onFocus={() => {
                  handleInputChange(undefined, record.key, "discount_per");
                }}
                onBlur={() => {
                  if (record.discount_per == null) {
                    handleInputChange(0, record.key, "discount_per");
                  }
                }}
                onChange={(value) => {
                  handleInputChange(value, record.key, "discount_per");
                  const discountAmount = bigPercent(record.rate, value || 0);
                  handleInputChange(discountAmount || 0, record.key, "discount");
                }}
                style={{ width: "100%" }}
                addonAfter={modeSelect}
              />
            );
          },
        },
      ]
      : []),
    ...(taxMode === "individual" && showTax
      ? [
        {
          title: `${taxLabel}`,
          dataIndex: "vat_per",
          width: 100,
          onHeaderCell: () => ({ style: { flex: 1 } }),
          render: (_, record) => (
            <InputNumberField
              min={0}
              max={100}
              controls={false}
              value={record.bonus ? 0 : record.vat_per}
              disabled
              onChange={(value) => handleInputChange(value, record.key, "vat_per")}
              style={{ width: "100%" }}
              suffix="%"
            />
          ),
        },
      ]
      : []),
    {
      title: "Total",
      dataIndex: "total",
      width: 200,
      onHeaderCell: () => ({ style: { flex: 1 } }),
      render: (_, record) => {
        const qty = toBig(record.quantity);
        const rate = toBig(record.rate);

        const line = bigMul(qty, rate);
        const discount = getItemDiscountAmount(record, line);
        const afterDiscount = toBig(line).minus(discount);
        const tax = showTax ? bigPercent(afterDiscount, record.vat_per) : toBig(0);

        const baseTotal = record.bonus
          ? toBig(0)
          : taxMode === "individual"
            ? afterDiscount.plus(tax)
            : line;

        const convertedTotal = conversionRate
          ? toBig(baseTotal).times(conversionRate)
          : toBig(baseTotal);

        return (
          <InputNumberField
            controls={false}
            value={parseFloat((convertedTotal || 0).toString()).toFixed(2)}
            formatter={(value) =>
              value !== undefined && value !== null ? parseFloat(value).toFixed(2) : "0.00"
            }
            disabled
            style={{ width: "100%" }}
            prefix={symbol}
          />
        );
      },
    },
    {
      title: "",
      dataIndex: "actions",
      render: (_, record) => (
        <Button
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDeleteRow(record.key)}
          disabled={itemsList.length === 1}
        />
      ),
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────────

  // Keep every input on the same line: the Product cell is taller (description
  // link + stock badge), so top-align all cells and centre the checkbox/delete
  // button against the 44px input height.
  const alignedColumns = columns.map((col) => {
    const needsInputHeight = col.dataIndex === "bonus" || col.dataIndex === "actions";
    return {
      ...col,
      onCell: (record, index) => ({
        ...(col.onCell ? col.onCell(record, index) : {}),
        style: { ...(col.onCell?.(record, index)?.style || {}), verticalAlign: "top" },
      }),
      ...(needsInputHeight && col.render
        ? {
            render: (...args) => (
              <div style={{ height: 44, display: "flex", alignItems: "center" }}>
                {col.render(...args)}
              </div>
            ),
          }
        : {}),
    };
  });
  return (
    <>
      {!convertId && !quoteId && (
        <Flex justify="end" style={{ marginBottom: "16px" }}>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} className="traic-button" onClick={handleAddRow}>
              Add Row
            </Button>
          </Col>
        </Flex>
      )}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {itemsList.map((record) => {
            const actionsCol = columns.find((c) => c.dataIndex === "actions");
            return (
              <div
                key={record.key}
                style={{
                  border: "1px solid #f0f0f0",
                  borderRadius: 8,
                  padding: 12,
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(100px, 1fr))",
                    gap: 8,
                  }}
                >
                  {columns
                    .filter((c) => c.dataIndex !== "actions")
                    .map((col) => (
                      <div
                        key={col.dataIndex || col.title}
                        style={
                          ["product", "packing_name", "batch_name"].includes(
                            col.dataIndex
                          )
                            ? { gridColumn: "1 / -1" }
                            : undefined
                        }
                      >
                        {col.title && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#8c8c8c",
                              marginBottom: 2,
                            }}
                          >
                            {col.title}
                          </div>
                        )}
                        {col.render(undefined, record)}
                      </div>
                    ))}
                </div>
                {actionsCol && (
                  <Flex justify="end" style={{ marginTop: 8 }}>
                    {actionsCol.render(undefined, record)}
                  </Flex>
                )}
              </div>
            );
          })}
          <DataTableFooter
            taxMode={taxMode}
            footerValues={footerValues}
            dataSource={itemsList}
            onFooterChange={handleFooterChange}
            defaultAccount={defaultAccount}
            conversionRate={conversionRate}
            dueDate={dueDate}
            authInfo={authInfo}
            pageType="sales"
            editId={editId}
          />
        </div>
      ) : (
        <Table
          dataSource={itemsList}
          columns={alignedColumns}
          scroll={{
            x: "max-content",
          }}
          pagination={false}
          footer={() => (
            <DataTableFooter
              taxMode={taxMode}
              footerValues={footerValues}
              dataSource={itemsList}
              onFooterChange={handleFooterChange}
              defaultAccount={defaultAccount}
              conversionRate={conversionRate}
              dueDate={dueDate}
              authInfo={authInfo}
              pageType="sales"
              editId={editId}
            />
          )}
        />
      )}
      <ProductQuickDrawer
        open={productDrawer.open}
        onClose={() => setProductDrawer({ open: false, rowKey: null })}
        onCreated={(product) => {
          if (!product) return;
          const productValue = {
            ...product,
            item_id: product.item_id,
            item_name: product.item_name,
            display_text: getItemDisplayName(product),
            tax_per: product.tax_per,
            sale_rate: product.sale_rate,
            raw: product,
          };
          handleInputChange(productValue, productDrawer.rowKey, "product");
          handleProductSelect({ raw: productValue }, productDrawer.rowKey);
        }}
      />
    </>
  );
};

export default InvoiceTable;